// Fallback segmentation for Thai tokens that miss the dictionary, e.g. the
// fused particle cluster นะครับ (no entry of its own, but นะ and ครับ both
// exist). The lexicon is generated from thai2english.db by
// scripts/generate-segmentation-wordlist.js.

// Keys are short to keep the bundled JSON small; the generator script writes
// this exact shape.
export interface SegmentationLexiconEntry {
   /** Phonetic respelling (t2e) */
   p: string
   /** Common meanings in display order (meaning, or explanation when absent) */
   m: string[]
   /** 1 when a sound file exists */
   s?: number
}

// Must match MIN_LENGTH/MAX_LENGTH in scripts/generate-segmentation-wordlist.js.
const MIN_WORD_LENGTH = 2
const MAX_WORD_LENGTH = 5

const MAX_TOKEN_LENGTH = 20

const PURE_THAI_REGEX = /^[฀-๿]+$/

/**
 * Splits an unknown token into a sequence of lexicon words covering the whole
 * token, preferring the fewest words (ties go to the longer first segment).
 * Returns null when the token isn't pure Thai, no full cover exists, or the
 * cover is a single word — the caller keeps its existing behavior, so this
 * can only ever improve a blank entry, never mangle a token.
 */
export function segmentUnknownWord(
   word: string,
   lexicon: Record<string, SegmentationLexiconEntry>
): string[] | null {
   if (word.length < MIN_WORD_LENGTH * 2 || word.length > MAX_TOKEN_LENGTH) return null
   if (!PURE_THAI_REGEX.test(word)) return null

   // bestFrom[i] = fewest lexicon words needed to cover word.slice(i), or
   // Infinity if uncovered. Built right-to-left; segment lengths are tried
   // longest-first so ties resolve to the longer first segment.
   const n = word.length
   const bestFrom: number[] = new Array(n + 1).fill(Infinity)
   const segmentLength: number[] = new Array(n + 1).fill(0)
   bestFrom[n] = 0

   for (let i = n - MIN_WORD_LENGTH; i >= 0; i--) {
      for (let len = Math.min(MAX_WORD_LENGTH, n - i); len >= MIN_WORD_LENGTH; len--) {
         if (bestFrom[i + len] === Infinity) continue
         if (!Object.prototype.hasOwnProperty.call(lexicon, word.slice(i, i + len))) continue
         if (bestFrom[i + len] + 1 < bestFrom[i]) {
            bestFrom[i] = bestFrom[i + len] + 1
            segmentLength[i] = len
         }
      }
   }

   if (bestFrom[0] === Infinity || bestFrom[0] < 2) return null

   const segments: string[] = []
   for (let i = 0; i < n; i += segmentLength[i]) {
      segments.push(word.slice(i, i + segmentLength[i]))
   }
   return segments
}

/**
 * Re-merges the LLM's over-split word spacing so a vetted dictionary compound
 * lands on a single token. The LLM splits compounds it doesn't know (พวก เขา,
 * ที่ นี่); this glues back exactly the runs that form a confirmed compound.
 *
 * Greedy longest-match, exact concatenation (the mirror of the offline
 * scripts/compound-segmentation/fix-spaced.js used on stored sentences): at
 * each token, extend the run while its concatenation is within maxCompoundLength
 * and take the LONGEST run that equals a `compounds` entry; otherwise pass the
 * token through untouched, so a standalone token is never swallowed. `compounds`
 * holds only pipeline-vetted compounds, so no false compound is ever formed.
 */
export function mergeVettedCompounds(
   spaced: string,
   compounds: Set<string>,
   maxCompoundLength: number
): string {
   const toks = spaced.split(' ').filter((t) => t.length > 0)
   const out: string[] = []
   let i = 0
   while (i < toks.length) {
      let acc = ''
      let bestEnd = -1 // exclusive index of the longest matching run
      for (let j = i; j < toks.length; j++) {
         acc += toks[j]
         if (acc.length > maxCompoundLength) break
         if (compounds.has(acc)) bestEnd = j + 1
      }
      if (bestEnd > i + 1) {
         out.push(toks.slice(i, bestEnd).join(''))
         i = bestEnd
      } else {
         out.push(toks[i])
         i += 1
      }
   }
   return out.join(' ')
}
